// src/shared/services/apiClient.js

import axios from 'axios';
import { BASE_URLS, ENV_CONFIG } from '../config/env';
import { getAuthToken } from '../utils/authStorage';

class ApiClient {
  constructor() {
    if (ApiClient.instance) {
      return ApiClient.instance;
    }

    this.client = axios.create({
      baseURL: BASE_URLS.MAIN,
      timeout: ENV_CONFIG.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        config.headers['device-platform'] = ENV_CONFIG.devicePlatform;

        const token = getAuthToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Log requests in development
        if (ENV_CONFIG.isDevelopment) {
          console.log('API Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            params: config.params,
            data: config.data,
          });
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Log responses in development
        if (ENV_CONFIG.isDevelopment) {
          console.log('API Response:', {
            status: response.status,
            url: response.config.url,
            data: response.data,
          });
        }
        return response;
      },
      (error) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );

    ApiClient.instance = this;
  }

  async retryRequest(requestFn, retries = 3) {
    let attempt = 0;
    
    while (attempt < retries) {
      try {
        return await requestFn();
      } catch (error) {
        if (attempt >= retries - 1) {
          throw error;
        }
        attempt++;
        
        // Optional: Add exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
    
    throw new Error('Max retries exceeded');
  }

  async get(path, queryParameters = null, retries = 1) {
    return this.retryRequest(
      () => this.client.get(path, { params: queryParameters }),
      retries
    );
  }

  async post(path, data = null, queryParameters = null, options = null, retries = 3) {
    return this.retryRequest(
      () => this.client.post(path, data, {
        params: queryParameters,
        ...options,
      }),
      retries
    );
  }

  async put(path, data = null, queryParameters = null, options = null, retries = 3) {
    return this.retryRequest(
      () => this.client.put(path, data, {
        params: queryParameters,
        ...options,
      }),
      retries
    );
  }

  async delete(path, data = null, queryParameters = null, options = null, retries = 3) {
    return this.retryRequest(
      () => this.client.delete(path, {
        data,
        params: queryParameters,
        ...options,
      }),
      retries
    );
  }

  async getPdfBytes(path, queryParameters = null, retries = 3) {
    try {
      const response = await this.retryRequest(
        () => this.client.get(path, {
          params: queryParameters,
          responseType: 'arraybuffer',
        }),
        retries
      );

      if (response.status === 200) {
        return new Uint8Array(response.data);
      } else {
        console.log(`Failed to get PDF: ${response.status}`);
        return null;
      }
    } catch (error) {
      console.log('Error getting PDF bytes:', error);
      return null;
    }
  }

  async uploadSingleFile({
    endpoint,
    imageFile = null,
    additionalData = null,
    keyName = 'display_picture',
  }) {
    console.log(`Uploading single image data: ${imageFile ? 'Data provided' : 'No data provided'}`);

    const formData = new FormData();

    // Add additional data
    if (additionalData) {
      Object.keys(additionalData).forEach((key) => {
        formData.append(key, additionalData[key]);
      });
    }

    // Add image file
    if (imageFile) {
      if (imageFile instanceof File) {
        formData.append(keyName, imageFile);
      } else if (imageFile instanceof Uint8Array) {
        const blob = new Blob([imageFile], { type: 'image/jpeg' });
        formData.append(keyName, blob, 'image.jpg');
      } else if (imageFile instanceof Blob) {
        const ext = imageFile.type === 'image/png' ? 'png' : 'jpg';
        formData.append(keyName, imageFile, `signature.${ext}`);
      }
    }

    return await this.client.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          console.error('Unauthorized error:', data);
          // Optional: Trigger logout or token refresh
          break;
        case 403:
          console.error('Forbidden:', data);
          break;
        case 404:
          console.error('Not found:', data);
          break;
        case 500:
          console.error('Server error:', data);
          break;
        default:
          console.error(`Error status ${status}:`, data);
      }
    } else if (error.request) {
      if (error.code === 'ECONNABORTED') {
        console.error('Connection timeout with API server');
      } else if (error.code === 'ERR_NETWORK') {
        console.error('Network error - check your connection');
      } else {
        console.error('No response received from server:', error.message);
      }
    } else if (error.message === 'canceled') {
      console.error('Request to API server was cancelled');
    } else {
      console.error('Error setting up request:', error.message);
    }
  }

  handleResponse(response) {
    try {
      if (response.data?.success === true) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;